/**
 * Full-bleed YouTube thumbnail marquee.
 * Drag starts only after a movement threshold so link clicks still navigate.
 */
if (!customElements.get('home-youtube-list')) {
  class HomeYoutubeList extends HTMLElement {
    connectedCallback() {
      this._viewport = this.querySelector('.home-youtube-bg');
      this._track = this.querySelector('.home-youtube-track');
      if (!this._viewport || !this._track) return;

      this._speed = parseInt(this._viewport.dataset.speed, 10) || 4000;
      this._offset = 0;
      this._setWidth = 0;
      this._lastViewportWidth = -1;
      this._slideCount = 0;
      this._paused = false;
      this._dragPending = false;
      this._dragging = false;
      this._dragMoved = false;
      this._suppressClick = false;
      this._hovering = false;
      this._lastTime = 0;
      this._raf = 0;
      this._dragThreshold = 8;

      this._onEnter = () => {
        this._hovering = true;
        this._paused = true;
      };
      this._onLeave = () => {
        this._hovering = false;
        if (!this._dragPending && !this._dragging) this._paused = false;
      };
      this._onPointerDown = (event) => this._pointerDown(event);
      this._onPointerMove = (event) => this._pointerMove(event);
      this._onPointerUp = (event) => this._pointerUp(event);
      this._onClick = (event) => this._handleClick(event);
      this._onResize = () => this._scheduleSetup();
      this._setupRaf = 0;

      this._setupTrack();
      this._viewport.addEventListener('mouseenter', this._onEnter);
      this._viewport.addEventListener('mouseleave', this._onLeave);
      this._viewport.addEventListener('pointerdown', this._onPointerDown);
      this._track.addEventListener('click', this._onClick, true);
      window.addEventListener('resize', this._onResize);
      this._resizeObserver = new ResizeObserver(this._onResize);
      this._resizeObserver.observe(this._viewport);

      this._lastTime = performance.now();
      this._raf = requestAnimationFrame((time) => this._tick(time));
    }

    disconnectedCallback() {
      cancelAnimationFrame(this._raf);
      cancelAnimationFrame(this._setupRaf);
      this._resizeObserver?.disconnect();
      this._viewport?.removeEventListener('mouseenter', this._onEnter);
      this._viewport?.removeEventListener('mouseleave', this._onLeave);
      this._viewport?.removeEventListener('pointerdown', this._onPointerDown);
      this._track?.removeEventListener('click', this._onClick, true);
      window.removeEventListener('resize', this._onResize);
      window.removeEventListener('pointermove', this._onPointerMove);
      window.removeEventListener('pointerup', this._onPointerUp);
    }

    _scheduleSetup() {
      if (this._setupRaf) cancelAnimationFrame(this._setupRaf);
      this._setupRaf = requestAnimationFrame(() => {
        this._setupRaf = 0;
        this._setupTrack();
      });
    }

    _originalSlides() {
      return [...this._track.querySelectorAll('[data-youtube-original]')];
    }

    _appendCloneSet(originals) {
      originals.forEach((slide) => {
        const clone = slide.cloneNode(true);
        clone.removeAttribute('data-youtube-original');
        clone.setAttribute('data-youtube-clone', '');
        clone.removeAttribute('data-shopify-editor-block');
        clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
        this._track.appendChild(clone);
      });
    }

    _setupTrack() {
      const originals = this._originalSlides();
      if (!originals.length) return;

      const first = originals[0];
      const last = originals[originals.length - 1];
      const gap = parseFloat(getComputedStyle(last).marginRight) || 0;
      const setWidth = last.offsetLeft + last.offsetWidth + gap - first.offsetLeft;
      const viewportWidth = this._viewport.clientWidth || 0;

      if (
        setWidth &&
        this._setWidth === setWidth &&
        this._lastViewportWidth === viewportWidth &&
        this._track.querySelector('[data-youtube-clone]')
      ) {
        return;
      }

      this._track.querySelectorAll('[data-youtube-clone]').forEach((node) => node.remove());

      this._setWidth = setWidth;
      this._lastViewportWidth = viewportWidth;
      this._slideCount = originals.length;
      if (!this._setWidth) return;

      // Always keep at least one extra copy. On mobile, original slides can already
      // exceed 2× viewport, so a viewport-only fill check would skip cloning and
      // leave a gap after the last slide during autoplay and drag.
      const fillUntil = this._setWidth + Math.max(viewportWidth * 2, this._setWidth) + 40;
      let guard = 0;
      while (this._track.scrollWidth < fillUntil && guard < 12) {
        this._appendCloneSet(originals);
        guard += 1;
      }

      this._wrapOffset();
      this._apply();
    }

    _wrapOffset() {
      const width = this._setWidth;
      if (!width) return;
      while (this._offset <= -width) this._offset += width;
      while (this._offset > 0) this._offset -= width;
    }

    _apply() {
      this._track.style.transform = `translate3d(${this._offset}px, 0, 0)`;
    }

    _tick(time) {
      const delta = Math.min(time - this._lastTime, 48);
      this._lastTime = time;

      if (!this._paused && !this._dragPending && !this._dragging && this._setWidth) {
        const slideWidth = this._setWidth / Math.max(this._slideCount, 1);
        this._offset -= (slideWidth / this._speed) * delta;
        this._wrapOffset();
        this._apply();
      }

      this._raf = requestAnimationFrame((next) => this._tick(next));
    }

    _bindPointerListeners() {
      window.addEventListener('pointermove', this._onPointerMove);
      window.addEventListener('pointerup', this._onPointerUp);
    }

    _unbindPointerListeners() {
      window.removeEventListener('pointermove', this._onPointerMove);
      window.removeEventListener('pointerup', this._onPointerUp);
    }

    _pointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      this._dragPending = true;
      this._dragging = false;
      this._dragMoved = false;
      this._dragStartX = event.clientX;
      this._dragStartY = event.clientY;
      this._dragStartOffset = this._offset;
      this._activePointerId = event.pointerId;

      this._bindPointerListeners();
    }

    _pointerMove(event) {
      if (!this._dragPending && !this._dragging) return;
      if (event.pointerId !== this._activePointerId) return;

      const dx = event.clientX - this._dragStartX;
      const dy = event.clientY - this._dragStartY;

      if (!this._dragging) {
        if (Math.hypot(dx, dy) < this._dragThreshold) return;

        this._dragging = true;
        this._dragPending = false;
        this._dragMoved = true;
        this._paused = true;
        this._viewport.setPointerCapture?.(event.pointerId);
      }

      this._offset = this._dragStartOffset + dx;
      this._wrapOffset();
      this._apply();
    }

    _pointerUp(event) {
      if (event.pointerId !== this._activePointerId) return;

      if (this._dragMoved) {
        this._suppressClick = true;
        window.setTimeout(() => {
          this._suppressClick = false;
        }, 0);
      }

      this._dragPending = false;
      this._dragging = false;
      this._paused = this._hovering;
      this._activePointerId = null;
      this._unbindPointerListeners();

      if (this._viewport.hasPointerCapture?.(event.pointerId)) {
        this._viewport.releasePointerCapture?.(event.pointerId);
      }
    }

    _handleClick(event) {
      if (!this._suppressClick) return;

      event.preventDefault();
      event.stopPropagation();
    }
  }

  customElements.define('home-youtube-list', HomeYoutubeList);
}
